import { useParams } from 'react-router';
import type { EmployeeWage } from 'src/api/EmployeeFinanceService';
import { useEmployeeWagesEntry } from '@stores/employeeFinanceStore';
import { useMemo } from 'react';

// Key: employeeId
export type WagesByEmployeeId = Record<
    string,
    { wageId: string; wage: number }
>;

const useEmployeeWages = (): {
    employeeWages: EmployeeWage[] | undefined;
    wagesByEmployeeId: WagesByEmployeeId;
    departmentId: string | undefined;
    isLoadingEmployeeWages: boolean;
    fetchEmployeeWages: () => void;
} => {
    const { departmentId } = useParams<{
        departmentId: string;
    }>();

    const {
        response: employeeWages,
        isLoading: isLoadingEmployeeWages,
        requestApiCall: fetchEmployeeWages,
    } = useEmployeeWagesEntry(departmentId);

    const wagesByEmployeeId = useMemo<WagesByEmployeeId>(() => {
        if (!employeeWages) {
            return {};
        }

        return employeeWages.reduce(
            (
                acc,
                { id: wageId, hourlyWage: wage, employee: { id: employeeId } },
            ) => {
                acc[employeeId] = { wageId, wage };
                return acc;
            },
            {},
        );
    }, [employeeWages]);

    return {
        employeeWages,
        wagesByEmployeeId,
        departmentId,
        isLoadingEmployeeWages,
        fetchEmployeeWages,
    };
};

export default useEmployeeWages;
